import React, {useCallback, useEffect, useRef, useState} from "react";
import axios from "axios";
import {Button} from "@material-ui/core";
import {isAuthenticated} from "../../Utils/Authentication";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import './style.scss';
import Input from "@material-ui/core/Input";
import Direction from "../../Utils/Direction/Direction";
import {hotjar} from "react-hotjar";
import LogoutIcon from "../../Utils/Icons/SVGs/LogoutIcon";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";

export default function Businesses(props) {
    const [businesses, setBusinesses] = useState(undefined);
    const [businessesToShow, setBusinessesToShow] = useState([]);
    const [isLoadingAuthentication, setIsLoadingAuthentication] = useState(true);
    const [searchText, setSearchText] = useState("");
    const inputRef = useRef();

    const onKeyDown = useCallback((event) => {
        if (String.fromCharCode(event.keyCode).match(/(\w|\s)/g)) {
            if (document.activeElement.id !== "user-name-input") {
                document.getElementById("user-name-input").focus();
            }
        }
    }, []);

    useEffect(() => {
        initComponent();

        document.addEventListener("keydown", onKeyDown, false);

        return () => {
            document.removeEventListener("keydown", onKeyDown, false);
        };
    }, []);

    async function initComponent() {
        const uid = await checkAuthentication();

        if (uid) {
            hotjar.identify(uid, {});
            initBusinessesOfUser(uid);
        }
    }

    async function checkAuthentication() {
        const authentication = await isAuthenticated();

        if (authentication.isAuthenticated) {
            setIsLoadingAuthentication(false);

            return authentication.uid
        } else {
            props.history.push('/login');

            return undefined;
        }
    }

    async function initBusinessesOfUser(uid) {
        const businessesIndex = {};
        const bid_uid = {};

        let bids;

        // Try-Catch so if the request will fail because of authentication, it'll redirect to login
        try {
            const {data: bidsTmp} = await axios.get('/api/v2a/getUserBusinesses', {
                params: {uid}
            });

            bids = bidsTmp
        } catch (e) {
            if (e.response && [401, 408].includes(e.response.status))
                window.location.replace("/login");
        }

        const businessesFetchingPromises = bids.map(bid => () => axios.get('/api/v2a/getBusiness', {params: {bid}}));
        const businessesFetchingConcurrency = 50;

        while (businessesFetchingPromises.length) {
            // getBusinessesConcurrency at at time
            const results = await Promise.all(businessesFetchingPromises.splice(0, businessesFetchingConcurrency).map(f => f()))

            for (let result of results) {
                const {data: business} = result;

                const bid = business.id;

                bid_uid[bid] = uid;
                businessesIndex[bid] = business;
            }
        }

        const businesses = Object.values(businessesIndex);

        if (businesses.length === 1) {
            goToDeliveryDashboard(businesses[0].id);

            return;
        }

        setBusinesses(businesses);
        initBusinessesToShow(businesses, searchText);
    }

    function isTextIntersects(t1, t2) {
        return t1.toLowerCase().includes(t2.toLowerCase()) || t2.toLowerCase().includes(t1.toLowerCase())
    }

    function initBusinessesToShow(businessesInput, searchTextInput) {
        const trimmedSearchText = searchTextInput.trim();

        setBusinessesToShow(trimmedSearchText ?
            businessesInput.filter(business => isTextIntersects(business.name, trimmedSearchText) || isTextIntersects(business.id, trimmedSearchText))
            :
            businessesInput);
    }

    function goToDeliveryDashboard(bid) {
        props.history.push(`/ship/${bid}`);
    }

    function onSearchTextChanged(newText) {
        setSearchText(newText);
        initBusinessesToShow(businesses, newText);
    }

    async function logout() {
        const {data} = await axios.post('/api/v2/users/logout');

        if (data.isLoggedOut)
            window.location.href = "/login";
    }

    if (!businesses || isLoadingAuthentication)
        return (
            <Direction direction="rtl">
                <div className="loading-container">
                    <CircularProgress size={80} thickness={2}/>
                    <div className="loading-businesses-text">
                        טוען עסקים...
                    </div>
                </div>
            </Direction>
        );

    return (
        <Direction direction="rtl">
            <div className="businesses-page">
                <IconButton className="logout-button"
                            onClick={() => logout()}>
                    <div className="logout-button-content">
                        <div className="logout-button-icon">
                            <LogoutIcon fontSize="27px"/>
                        </div>
                    </div>
                </IconButton>
                <Input id="user-name-input"
                       placeholder="חיפוש שם עסק"
                       value={searchText}
                       onChange={e => onSearchTextChanged(e.target.value)}
                       className="user-name-input"
                       ref={inputRef}
                />
                <div className="businesses-list">
                    {businessesToShow.map(business =>
                        <Button className="business-button"
                                key={business.id}
                                variant="outlined"
                                onClick={() => goToDeliveryDashboard(business.id)}>
                            {business.name}
                        </Button>
                    )}
                </div>
            </div>
        </Direction>
    );
}